import { useEffect, useRef, useState } from 'react';
import { DownloadOutlined, UploadFileOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import {
  AttachmentField,
  AttachmentItem,
  DetailDrawer,
  LoadingIndicator,
  DetailDrawerBody,
  DetailSection,
  FormAutocomplete,
  FormDialog,
  FeedbackSnackbar,
  FormTextField,
  ResourcePage,
  ResourceCollection,
  StatusBadge,
  TableIdentityCell,
  ValueBadge,
  designTokens,
  useFeedbackSnackbar,
  type ResourceCollectionState,
  type StatusTone,
} from '../../../design-system';
import { fixtureDocuments } from '../fixtures';
import { documentTypeLabel, documentTypeOptions, mapDocument, type DocumentRow, type DocumentSubjectOption, type HRDocument, type UploadDocumentInput } from '../model';
import type { DocumentsPort } from '../port';

function documentStatusPresentation(status: string): { label: string; tone: StatusTone } {
  return status === 'CLEAN'
    ? { label: '안전', tone: 'success' }
    : status === 'INFECTED'
      ? { label: '악성 파일', tone: 'danger' }
      : { label: '검사 중', tone: 'warning' };
}

export type DocumentsPageProps = {
  port: DocumentsPort;
  fixtureMode: boolean;
  canUpload: boolean;
  canDownload: boolean;
  canSearchSubjects?: Partial<Record<UploadDocumentInput['subjectType'], boolean>>;
  loadSubjects?: (subjectType: UploadDocumentInput['subjectType'], query: string) => Promise<DocumentSubjectOption[]>;
};

export function DocumentsPage({ port, fixtureMode, canUpload, canDownload, canSearchSubjects = {}, loadSubjects }: DocumentsPageProps) {
  const resource = useAsyncResource<DocumentRow[]>(async () => {
    const documents = fixtureMode ? fixtureDocuments : await port.list();
    return documents.map(mapDocument);
  }, [port, fixtureMode]);
  const values = resource.data ?? [];
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<HRDocument>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string>();
  const [subjectType, setSubjectType] = useState<UploadDocumentInput['subjectType']>('WORKER');
  const [subject, setSubject] = useState<DocumentSubjectOption>();
  const [subjectQuery, setSubjectQuery] = useState('');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [customType, setCustomType] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [documentTypeQuery, setDocumentTypeQuery] = useState('');
  const { notify, feedbackProps } = useFeedbackSnackbar();
  const detailRequest = useRef(0);
  useEffect(() => {
    const timeout = window.setTimeout(() => setSubjectSearch(subjectQuery.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [subjectQuery]);
  const canSearchSubject = fixtureMode || Boolean(canSearchSubjects[subjectType]);
  const subjects = useAsyncResource(async () => {
    if (!dialogOpen || !canSearchSubject || (!fixtureMode && !loadSubjects)) return [];
    if (fixtureMode) return [{ id: subjectType === 'WORKER' ? '03' : 'person-03', name: '민서(김민서)', detail: 'Backend Engineer · Platform Engineering' }].filter((item) => `${item.name} ${item.detail}`.toLowerCase().includes(subjectSearch.toLowerCase()));
    return loadSubjects!(subjectType, subjectSearch);
  }, [dialogOpen, fixtureMode, canSearchSubject, loadSubjects, subjectType, subjectSearch]);
  const availableTypes = [...documentTypeOptions, ...Array.from(new Set(values.map((row) => row.source.documentType)))
    .filter((value) => !documentTypeOptions.some((option) => option.value === value))
    .map((value) => ({ value, label: documentTypeLabel(value) }))];
  const selectedType = documentType === '__CUSTOM__' ? customType.trim() : documentType;
  const openUpload = () => { setSubmitError(undefined); setDialogOpen(true); };
  const closeUpload = () => { if (!submitLoading) setDialogOpen(false); };
  const closeDetail = () => { detailRequest.current += 1; setDetailOpen(false); };

  const [retentionUntil, setRetentionUntil] = useState('');
  const [file, setFile] = useState<File>();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [downloadError, setDownloadError] = useState<{ documentId: string; name: string; message: string }>();
  const [downloadingId, setDownloadingId] = useState<string>();
  const downloadPending = useRef(false);
  const rows = values.filter((row) => `${row.name} ${row.subject} ${row.documentType}`.toLowerCase().includes(query.toLowerCase()) && (statusFilter === '' || row.scanStatus === statusFilter));
  const openDetail = (row: DocumentRow) => {
    const request = ++detailRequest.current;
    setDetailOpen(true);
    setSelected(row.source);
    setDetailError(undefined);
    setDetailLoading(true);
    const load = fixtureMode
      ? Promise.resolve(fixtureDocuments.find((document) => document.id === row.id) ?? row.source)
      : port.get(row.id);
    void load.then((document) => { if (request === detailRequest.current) setSelected(document); }, (reason: unknown) => { if (request === detailRequest.current) setDetailError(reason instanceof Error ? reason.message : '문서 정보를 불러오지 못했습니다.'); }).finally(() => { if (request === detailRequest.current) setDetailLoading(false); });
  };
  const download = (row: DocumentRow) => {
    if (!canDownload || row.scanStatus !== 'CLEAN' || downloadPending.current) return;
    downloadPending.current = true;
    setDownloadingId(row.id);
    const load = fixtureMode ? Promise.resolve(new Blob(['fixture document'], { type: 'application/pdf' })) : port.download(row.id);
    void load.then((blob) => {
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = row.name;
      anchor.click();
      URL.revokeObjectURL(url);
      setDownloadError((current) => current?.documentId === row.id ? undefined : current);
      notify('다운로드를 시작했습니다.', { tone: 'success' });
    }, (reason: unknown) => setDownloadError({ documentId: row.id, name: row.name, message: reason instanceof Error ? reason.message : '문서를 다운로드하지 못했습니다. 다시 시도해 주세요.' }))
      .finally(() => { downloadPending.current = false; setDownloadingId(undefined); });
  };
  const upload = () => {
    if (!canUpload || !canSearchSubject) return;
    if (!subject || !selectedType || !file) {
      setSubmitError('대상, 문서 유형, 파일을 모두 입력해 주세요.');
      return;
    }
    setSubmitLoading(true);
    setSubmitError(undefined);
    const submit = fixtureMode ? Promise.resolve() : port.upload({ subjectType, subjectId: subject!.id, documentType: selectedType, retentionUntil: retentionUntil || undefined, file });
    void submit.then(() => {
      setDialogOpen(false);
      setSubject(undefined); setSubjectQuery(''); setSubjectSearch(''); setDocumentType(''); setDocumentTypeQuery(''); setCustomType(''); setRetentionUntil(''); setFile(undefined);
      notify('문서를 등록했습니다. 보안 검사가 끝나면 다운로드할 수 있습니다.', { tone: 'success' });
      resource.reload();
    }, (reason: unknown) => setSubmitError(reason instanceof Error ? reason.message : '문서를 등록하지 못했습니다.')).finally(() => setSubmitLoading(false));
  };
  const columns: GridColDef<DocumentRow>[] = [
    { field: 'name', headerName: '문서명', minWidth: 220, flex: 1.3, renderCell: ({ row }) => <TableIdentityCell primary={row.name} secondary={`${row.source.contentType} · ${Math.max(1, Math.round(row.source.sizeBytes / 1024))}KB`} /> },
    { field: 'subject', headerName: '대상', minWidth: 150, flex: 0.8, renderCell: ({ row }) => <><Typography variant="body2" sx={{ fontWeight: 650 }}>{row.subject}</Typography><Box component="span" sx={{ ml: 0.75 }}><ValueBadge label={row.subjectType} size="compact" /></Box></> },
    { field: 'documentType', headerName: '문서 유형', minWidth: 140, flex: 0.7, renderCell: ({ value }) => <ValueBadge label={value} maxWidth="100%" /> },
    { field: 'createdAt', headerName: '등록일', width: 110 },
    { field: 'scanStatus', headerName: '보안 검사', width: 105, renderCell: ({ value }) => { const status = documentStatusPresentation(String(value)); return <StatusBadge label={status.label} tone={status.tone} />; } },
    { field: 'actions', headerName: '', width: 92, sortable: false, renderCell: ({ row, tabIndex }) => canDownload && row.scanStatus === 'CLEAN' ? <Button size="small" tabIndex={tabIndex} disabled={Boolean(downloadingId)} startIcon={<DownloadOutlined />} onClick={(event) => { event.stopPropagation(); download(row); }}>다운로드</Button> : <Typography variant="caption" color={row.scanStatus === 'INFECTED' ? 'error.main' : 'text.secondary'}>{row.scanStatus === 'INFECTED' ? '차단됨' : row.scanStatus === 'PENDING' ? '검사 후 가능' : ''}</Typography> },
  ];
  const collectionState: ResourceCollectionState = resource.loading
    ? { kind: 'loading' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : rows.length === 0
        ? { kind: 'empty', title: values.length ? '검색 조건에 맞는 문서가 없습니다.' : '등록된 문서가 없습니다.', action: !values.length && canUpload ? <Button variant="fill" startIcon={<UploadFileOutlined />} onClick={openUpload}>문서 등록</Button> : undefined }
        : { kind: 'ready' };
  const downloadErrorInDetail = Boolean(detailOpen && selected?.id === downloadError?.documentId);
  const retryDocument = detailOpen && selected?.id === downloadError?.documentId
    ? selected && mapDocument(selected)
    : values.find((row) => row.id === downloadError?.documentId);
  const downloadErrorNotice = downloadError && <Alert severity="error"
    action={canDownload && retryDocument?.scanStatus === 'CLEAN' ? <Button color="inherit" disabled={Boolean(downloadingId) || (downloadErrorInDetail && (detailLoading || Boolean(detailError)))} onClick={() => download(retryDocument)}>다시 시도</Button> : undefined}
  >{downloadError.name} · {downloadError.message}</Alert>;

  return <><ResourcePage
    title="문서"
    refreshAction={{ onClick: resource.reload, loading: resource.loading }}
    primaryAction={canUpload ? <Button variant="fill" startIcon={<UploadFileOutlined />} onClick={openUpload}>문서 등록</Button> : undefined}
  >
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {downloadError && !downloadErrorInDetail && <Box sx={{ mb: 1.5, flexShrink: 0 }}>{downloadErrorNotice}</Box>}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResourceCollection
          state={collectionState}
          rows={rows}
          totalCount={values.length}
          columns={columns}
          query={query}
          onQueryChange={setQuery}
          searchPlaceholder="문서명·대상·유형 검색"
          ariaLabel="문서 목록"
          filters={[{ label: '보안 검사', value: statusFilter, onChange: setStatusFilter, options: [{ label: '전체', value: '' }, { label: '검사 중', value: 'PENDING' }, { label: '안전', value: 'CLEAN' }, { label: '악성 파일', value: 'INFECTED' }] }]}
          onRowClick={({ row }) => openDetail(row)}
        />
      </Box>
    </Box>
  </ResourcePage>
    <FormDialog
      open={canUpload && dialogOpen}
      onClose={closeUpload}
      onSubmit={upload}
      title="문서 등록"
      help="등록한 파일은 악성코드 검사가 완료된 뒤 다운로드할 수 있습니다."
      cancelLabel="취소"
      submitLabel="등록"
      busy={submitLoading}
      busyLabel="등록 중"
    >
        {submitError && <Alert severity="error">{submitError}</Alert>}
        <FormTextField select label="등록 대상" value={subjectType} disabled={submitLoading} onChange={(event) => { setSubjectType(event.target.value as UploadDocumentInput['subjectType']); setSubject(undefined); setSubjectQuery(''); setSubjectSearch(''); }}><MenuItem value="WORKER">구성원</MenuItem><MenuItem value="PERSON">개인</MenuItem></FormTextField>
        <FormAutocomplete
          options={canSearchSubject ? subjects.data ?? [] : []}
          value={subject ?? null}
          inputValue={subject?.name ?? subjectQuery}
          disabled={submitLoading || !canSearchSubject}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options) => options}
          request={subjects.error ? { status: 'error', message: '대상 정보를 조회하지 못했습니다. 다시 검색해 주세요.', onRetry: subjects.reload } : { status: subjects.loading ? 'loading' : 'ready' }}
          onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setSubjectQuery(value); if (reason === 'input') setSubject(undefined); } }}
          onChange={(_, value) => { setSubject(value ?? undefined); setSubmitError(undefined); }}
          renderOption={({ key, ...props }, option) => <Box component="li" key={key} {...props}><Stack><Typography variant="body2">{option.name}</Typography>{option.detail && <Typography variant="caption" color="text.secondary">{option.detail}</Typography>}</Stack></Box>}
          input={{ label: '대상 검색', required: true, placeholder: subjectType === 'PERSON' ? '이름으로 검색' : '이름·직무·소속으로 검색', helperText: !canSearchSubject ? subjectType === 'PERSON' ? '대상 선택에는 개인 및 개인 이름 조회 권한이 필요합니다.' : '대상 선택에는 구성원 조회 권한이 필요합니다.' : undefined }}
        />
        {subject && <Alert severity="info">{subject.name}{subject.detail ? ` · ${subject.detail}` : ''}에게 문서를 등록합니다.</Alert>}
        <FormAutocomplete options={[...availableTypes, { value: '__CUSTOM__', label: '직접 입력' }]}
          value={documentType === '__CUSTOM__' ? { value: '__CUSTOM__', label: '직접 입력' } : availableTypes.find((option) => option.value === documentType) ?? null}
          inputValue={documentType === '__CUSTOM__' ? '직접 입력' : availableTypes.find((option) => option.value === documentType)?.label ?? documentTypeQuery}
          disabled={submitLoading} getOptionLabel={(option) => option.label} getOptionKey={(option) => option.value} isOptionEqualToValue={(option, value) => option.value === value.value}
          onInputChange={(_, value, reason) => { if (reason === 'input' || reason === 'clear') { setDocumentTypeQuery(value); setDocumentType(''); } }}
          onChange={(_, option) => { setDocumentTypeQuery(''); setDocumentType(option?.value ?? ''); }}
          input={{ label: '문서 유형', required: true, placeholder: '문서 유형 이름으로 검색' }} />
        {documentType === '__CUSTOM__' && <FormTextField label="새 문서 유형" required value={customType} disabled={submitLoading} help="기존 분류가 없을 때만 새 유형을 입력합니다." onChange={(event) => setCustomType(event.target.value)} />}
        <FormTextField type="date" label="보존 기한" value={retentionUntil} onChange={(event) => setRetentionUntil(event.target.value)} />
        <AttachmentField label="첨부 파일" required busy={submitLoading} busyLabel="문서를 등록하고 있습니다." actionLabel="파일 선택" onFilesSelected={(files) => {
          if (files.length > 1) { setSubmitError('파일은 한 개씩 선택해 주세요.'); return; }
          setFile(files[0]); setSubmitError(undefined);
        }}>
          {file ? <AttachmentItem name={file.name} description={`${Math.max(1, Math.round(file.size / 1024))}KB`} disabled={submitLoading} onRemove={() => setFile(undefined)} /> : null}
        </AttachmentField>
    </FormDialog>
    <FeedbackSnackbar {...feedbackProps} />
    {selected && <DetailDrawer open={detailOpen} onClose={closeDetail} title="문서 정보" meta={selected.displayName} footer={canDownload && selected.scanStatus === 'CLEAN' ? <Button variant="contained" disabled={detailLoading || Boolean(detailError) || Boolean(downloadingId)} startIcon={<DownloadOutlined />} onClick={() => download(mapDocument(selected))}>다운로드</Button> : undefined}>
      <DetailDrawerBody padding="none">
        {downloadErrorInDetail && <Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 1.5 }}>{downloadErrorNotice}</Box>}
        {detailLoading ? <Box sx={{ height: 180, display: 'grid', placeItems: 'center' }}><LoadingIndicator label="문서 상세 불러오는 중" /></Box> : detailError ? <Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 1.5 }}><Alert severity="error" action={<Button color="inherit" onClick={() => openDetail(mapDocument(selected))}>다시 시도</Button>}>{detailError}</Alert></Box> : <><Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 1.5 }}><Stack direction="row" sx={{ alignItems: 'center' }}><Box sx={{ flex: 1 }}><Typography variant="h6">{selected.displayName}</Typography><Typography variant="caption" color="text.secondary">{selected.contentType} · {Math.max(1, Math.round(selected.sizeBytes / 1024))}KB</Typography></Box><StatusBadge label={documentStatusPresentation(selected.scanStatus).label} tone={documentStatusPresentation(selected.scanStatus).tone} /></Stack></Box><Divider /><DetailSection title="분류" rows={[["대상 유형", <ValueBadge label={mapDocument(selected).subjectType} />], ['대상', mapDocument(selected).subject], ['문서 유형', <ValueBadge label={documentTypeLabel(selected.documentType)} />], ['등록일', mapDocument(selected).createdAt], ['보존 기한', selected.retentionUntil || '미지정']]} /><Divider /><DetailSection title="보안" rows={[["검사 상태", <StatusBadge {...documentStatusPresentation(selected.scanStatus)} />], ['SHA-256', selected.sha256]]} />{selected.scanStatus === 'INFECTED' && <Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 1.5, pt: 0 }}><Alert severity="error">악성 파일로 판정되어 다운로드가 차단되었습니다.</Alert></Box>}{selected.scanStatus === 'PENDING' && <Box sx={{ px: `${designTokens.layout.detailInlineInset}px`, py: 1.5, pt: 0 }}><Alert severity="warning">악성코드 검사가 진행 중입니다. 완료되면 다운로드할 수 있습니다.<Button size="small" disabled={detailLoading} onClick={() => { openDetail(mapDocument(selected)); resource.reload(); }}>검사 상태 확인</Button></Alert></Box>}</>}
      </DetailDrawerBody>
    </DetailDrawer>}
  </>;
}
